import { html, css } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { BaseVisualization, ReportEvent, VisualizationOptions } from "./baseVisualization";
import { getThemeManager, utils } from "@omegagrid/core";
import { DeepPartial } from "../../types";
import { loader } from "@omegagrid/plugin-charts";
import { ChartLegendAlignment, ChartLegendOptions, ChartLegendPosition, ChartSeriesDataLabels, ChartViewOptions, ContextData } from "@reporting/rp-types/analytics";
import { GridModel } from "@omegagrid/grid";
import { CHART_AXES, ChartAxisKey, HIGHCHARTS_AXES, createGridModel, evaluateExpression, formatColor, getPlotBandLabel, getPlotBands } from "./viewOptions";
import { getChartTheme, getDataLabelShapeStyle, getPlotBandColor, getPlotBandLabelStyle } from "./chartTheme";
import { ChartController } from "./chartController";
import type { ReportContext } from "@reporting/rp-types/analytics";
import type { Options, Chart, Point, LegendOptions } from 'highcharts';

export abstract class ChartAdapter {
	abstract getChart(): Promise<DeepPartial<Options>>|DeepPartial<Options>;
	/** report context of the rendered data, enables the drill down */
	reportContext?: ReportContext;
}

/** click on a chart data point, the drill controller listens to it */
export class ChartPointEvent extends Event {

	constructor(
		public readonly point: Point,
		public readonly browserEvent: MouseEvent
	) {
		super('point.click');
	}

}

const DEFAULT_CHART_OPTIONS: DeepPartial<Options> = {
	chart: {
		styledMode: false,
	},
	credits: {
		enabled: false
	}
};

const LEGEND_POSITIONS: Record<ChartLegendPosition, DeepPartial<LegendOptions>> = {
	'top': {layout: 'horizontal', align: 'center', verticalAlign: 'top'},
	'bottom': {layout: 'horizontal', align: 'center', verticalAlign: 'bottom'},
	'left': {layout: 'vertical', align: 'left', verticalAlign: 'middle'},
	'right': {layout: 'vertical', align: 'right', verticalAlign: 'middle'},
};

const LEGEND_ALIGNMENTS: Record<ChartLegendAlignment, {horizontal: string, vertical: string}> = {
	'start': {horizontal: 'left', vertical: 'top'},
	'center': {horizontal: 'center', vertical: 'middle'},
	'end': {horizontal: 'right', vertical: 'bottom'},
};

@customElement('rp-visual-chart')
export class ChartVisualization extends BaseVisualization {

	static styles = [BaseVisualization.styles, css`
		:host {
			width: 100%;
			height: 100%;
			position: relative;
			overflow: hidden;
		}

		.chart {
			position: absolute;
			top: 0;
			left: 0;
			right: -1px;
			bottom: 0;
		}
	`];

	@property({type: Object})
	adapter: ChartAdapter;

	@query('.chart')
	wrapper: HTMLDivElement;

	@state()
	chartData: DeepPartial<Options>;

	private chart: Chart;

	private _controller: ChartController;

	private _gridModel: GridModel;

	/** report data model, evaluates the cell references used in the view options */
	get gridModel() {
		return this._gridModel ??= createGridModel(this.reportData, {context: this.contextData as ContextData});
	}

	/** user defined options of the view, edited by rp-visual-chart-editor */
	get viewOptions(): ChartViewOptions {
		return (this.contextData?.view_options ?? {}) as ChartViewOptions;
	}

	/**
	 * User defined plot bands of the axis with evaluated from/to expressions.
	 * Bounds of the value axis bands are values, bounds of the category axis
	 * bands are category indexes (as Highcharts reads them).
	 */
	getPlotBands(axis: ChartAxisKey = 'y_axis') {
		const axisOptions = this.viewOptions[axis];
		return getPlotBands(axisOptions?.plot_bands, axisOptions?.plot_bands_defaults, () => this.gridModel);
	}

	/**
	 * User defined axis options (title, range, labels) and the plot bands,
	 * applied over the axis of the adapter options. A chart with several axes
	 * of the kind gets them on the first one; the axis is merged into a new
	 * object to keep the adapter options untouched.
	 */
	private applyAxis(options: DeepPartial<Options>, axisKey: ChartAxisKey) {
		const {title, min, max, labels, tick_interval, gridline_width, gridline_color} = this.viewOptions[axisKey] ?? {};
		const bands = this.getPlotBands(axisKey);
		const patch: Record<string, any> = {};

		if (gridline_width != null) patch.gridLineWidth = gridline_width;
		if (gridline_color) patch.gridLineColor = formatColor(gridline_color);

		if (bands.length) {
			// a band without an own color gets the subtle theme fill
			patch.plotBands = bands.map(band => ({
				from: band.from,
				to: band.to,
				color: band.color ? formatColor(band.color) : getPlotBandColor(this),
				label: getPlotBandLabel(band.label, getPlotBandLabelStyle(this)),
			}));
		}

		if (title) patch.title = {text: title, enabled: true};
		if (labels != null) patch.labels = {enabled: labels};
		if (tick_interval) patch.tickInterval = tick_interval;

		const range = (value: string|number) => {
			const evaluated = Number(evaluateExpression(value, () => this.gridModel));
			return isFinite(evaluated) ? evaluated : undefined;
		};
		if (min != null && min !== '') patch.min = range(min);
		if (max != null && max !== '') patch.max = range(max);

		if (!Object.keys(patch).length) return;

		const name = HIGHCHARTS_AXES[axisKey];
		const axes = options as any;
		const current = Array.isArray(axes[name]) ? axes[name][0] : axes[name];
		const merged = utils.mergeDeep<any>({}, current ?? {}, patch);
		if (Array.isArray(axes[name])) axes[name][0] = merged;
		else axes[name] = merged;
	}

	/** names of the rendered series, the series options are indexed the same way */
	get seriesNames(): string[] {
		return ((this.chartData?.series ?? []) as {name?: string}[]).map(serie => serie?.name ?? '');
	}

	/**
	 * Effective color of each rendered series - the own color of the series,
	 * falling back to the series defaults.
	 */
	getSeriesColors(): string[] {
		const defaults = this.viewOptions.series_defaults;
		const series = this.viewOptions.series;
		return this.seriesNames.map((_, i) => series?.[i]?.color || defaults?.color);
	}

	/**
	 * Effective color of the negative part of each rendered series - the own
	 * value of the series, falling back to the series defaults.
	 */
	getSeriesNegativeColors(): string[] {
		const defaults = this.viewOptions.series_defaults;
		const series = this.viewOptions.series;
		return this.seriesNames.map((_, i) => series?.[i]?.negative_color || defaults?.negative_color);
	}

	/**
	 * Effective data label options of each rendered series - the own values
	 * over the series defaults.
	 */
	getSeriesDataLabels(): ChartSeriesDataLabels[] {
		const defaults = this.viewOptions.series_defaults?.data_labels;
		const series = this.viewOptions.series;
		return this.seriesNames.map((_, i) => ({...defaults, ...series?.[i]?.data_labels}));
	}

	/**
	 * User defined series options merged into the series generated from the
	 * report data - the defaults first, the per-index options over them. The
	 * color and data_labels are not merged directly - the colors resolve
	 * through the defaults chain (see getSeriesColors) and data_labels
	 * translates to highcharts dataLabels.
	 */
	private applySeries(options: DeepPartial<Options>) {
		const {color: _defaultColor, negative_color: _defaultNegative, data_labels: _defaultLabels, ...defaultOptions} = this.viewOptions.series_defaults ?? {};
		const series = this.viewOptions.series;
		const colors = this.getSeriesColors();
		const negativeColors = this.getSeriesNegativeColors();
		const labels = this.getSeriesDataLabels();
		const hasLabels = labels.some(label => label.enabled != null || label.shape);
		if (!series?.length && !Object.keys(defaultOptions).length && !colors.some(Boolean) && !negativeColors.some(Boolean) && !hasLabels) return;

		options.series = ((options.series ?? []) as any[]).map((serie, i) => {
			const {color: _ownColor, negative_color: _ownNegative, data_labels: _ownLabels, ...serieOptions} = series?.[i] ?? {};
			const label = labels[i] ?? {};
			const hasLabel = label.enabled != null || label.shape;
			if (!colors[i] && !negativeColors[i] && !hasLabel && !Object.keys(defaultOptions).length && !Object.keys(serieOptions).length) return serie;

			const merged = utils.mergeDeep<any>({}, serie, defaultOptions, serieOptions);
			if (colors[i]) merged.color = formatColor(colors[i]);
			if (negativeColors[i]) merged.negativeColor = formatColor(negativeColors[i]);
			if (hasLabel) merged.dataLabels = utils.mergeDeep({}, merged.dataLabels ?? {}, {
				...(label.enabled != null ? {enabled: label.enabled} : {}),
				// with a shape the label box has to become visible
				...(label.shape ? {shape: label.shape, padding: 4, ...getDataLabelShapeStyle(this)} : {}),
			});
			return merged;
		});
	}

	get legendOptions(): ChartLegendOptions {
		return this.viewOptions.legend ?? {};
	}

	/**
	 * Legend visibility and placement, applied over the options of the adapter
	 * (which shows a legend at the bottom for more than one series).
	 */
	private applyLegend(options: DeepPartial<Options>) {
		const {enabled, position, alignment} = this.legendOptions;
		const legend: Record<string, any> = {};

		if (enabled != null) legend.enabled = enabled;
		if (position) Object.assign(legend, LEGEND_POSITIONS[position]);

		if (alignment) {
			// the alignment runs along the side the legend sits at - horizontally
			// for the top/bottom position, vertically for the side ones
			const vertical = position === 'left' || position === 'right';
			const align = LEGEND_ALIGNMENTS[alignment];
			legend[vertical ? 'verticalAlign' : 'align'] = vertical ? align.vertical : align.horizontal;
		}

		if (!Object.keys(legend).length) return;
		Object.assign(options.legend ??= {}, legend);
	}

	/**
	 * Chart options of the adapter with the user defined view options applied,
	 * over the theme resolved from the active og theme variables.
	 */
	createChartOptions(): DeepPartial<Options> {
		let options = utils.mergeDeep<DeepPartial<Options>>({}, getChartTheme(this), this.chartData, DEFAULT_CHART_OPTIONS);
		CHART_AXES.forEach(axis => this.applyAxis(options, axis));
		this.applyLegend(options);
		this.applySeries(options);
		this.applyDrill(options);

		if (this.viewOptions.zooming) {
			(options.chart ??= {}).zooming = {type: this.viewOptions.zooming};
		}

		// raw highcharts options of the user (the json tab of the editor)
		// merged as the last step - they win over everything generated
		if (this.viewOptions.highchart_options) {
			options = utils.mergeDeep({}, options, this.viewOptions.highchart_options);
		}

		return options;
	}

	/**
	 * Point clicks bubble as component events the drill controller listens to
	 * (see ChartController). Only with a report context - a chart without one
	 * (e.g. a custom adapter) has nothing to drill into.
	 */
	private applyDrill(options: DeepPartial<Options>) {
		if (!this.adapter?.reportContext) return;
		const component = this;
		const series: any = ((options.plotOptions ??= {}) as any).series ??= {};
		series.cursor = 'pointer';
		(series.point ??= {}).events = {
			...(series.point.events ?? {}),
			click: function(e: MouseEvent) {
				component.dispatchEvent(new ChartPointEvent(this as Point, e));
			},
		};
	}

	refresh(contextData?: ContextData) {
		super.refresh(contextData);
		if (contextData) {
			this.reloadChartData();
			this.dispatchEditEvent();
		}
	}

	/**
	 * Re-query the adapter: view options may change the generated chart data
	 * itself (e.g. inverted axes), not just the presentation on top of it.
	 */
	private async reloadChartData() {
		if (this.adapter) {
			this.chartData = await this.adapter.getChart();
		}
		this.requestUpdate();
	}

	async willUpdate(props: Map<PropertyKey, unknown>) {
		this._gridModel = null;
		if (props.has('adapter') && this.adapter) {
			if (!this._controller && this.adapter.reportContext) {
				this._controller = new ChartController(this, this.adapter.reportContext);
			}
			this.chartData = await this.adapter.getChart();
		}
	}

	/** theme colors are baked into the rendered svg, a theme switch re-renders */
	private readonly onThemeChange = () => this.requestUpdate();

	connectedCallback() {
		super.connectedCallback();
		getThemeManager().addEventListener('change', this.onThemeChange);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		getThemeManager().removeEventListener('change', this.onThemeChange);
	}

	async updated(props: Map<string, any>) {
		this.chart = null;
		const Highcharts = await loader.get();
		const data = this.createChartOptions();
		this.chart = Highcharts.chart(this.wrapper as HTMLElement, data as Options)

		if (props.has('chartData')) {
			this.refresh();
			this.dispatchEvent(new ReportEvent('load', {visualization: this}));
		}
	}

	layout() {
		this.chart?.reflow();
	}

	render = () => html`<div class="chart"></div>`;

	getOptions(): VisualizationOptions {
		return this.createOptions({edit: true});
	}

}
